import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, SignOut, BookmarkSimple, CaretRight, GearSix } from "@phosphor-icons/react";
import { sortHeights } from "@/lib/heights";
import { WAIST_SIZES, SHOE_SIZES } from "@/lib/sizes";
import { track, setAnalyticsUser } from "@/lib/track";
import CityInput from "@/components/CityInput";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const USERNAME_RE = /^[A-Za-z]{3,20}$/;
const GENDER_TO_CATEGORY = { male: "men", female: "women" };

const labelCls = "block text-xs uppercase tracking-[0.2em] mb-2 text-[#6B6A68]";
const inputCls =
  "w-full border border-[#E4E1D9] rounded-md px-4 py-3 text-[15px] text-[#2D302A] outline-none focus:border-[#2D302A] transition-colors bg-white";
const mono = { fontFamily: "IBM Plex Mono, monospace" };

/**
 * Profile page — replaces the old edit-profile modal with a real, app-like
 * screen. Shows the avatar/username header, the entry point into Saved
 * bookmarks (which used to be its own nav tab), lets the user edit their
 * details, and holds the Log out action. Redirects to /login when signed out.
 */
const Profile = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  // null until the count lands (or if it fails) — the row renders either way.
  const [bookmarkCount, setBookmarkCount] = useState(null);
  const [form, setForm] = useState({
    username: "",
    name: "",
    height: "",
    weight: "",
    weight_unit: "lbs",
    waist_size: "",
    shoe_size: "",
    city: "",
  });

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        const u = res.data;
        setUser(u);
        setForm({
          username: u.username || "",
          name: u.name || "",
          height: u.height || "",
          weight: u.weight || "",
          weight_unit: u.weight_unit || "lbs",
          waist_size: u.waist_size || "",
          shoe_size: u.shoe_size || "",
          city: u.city || "",
        });
      })
      .catch(() => {
        if (!cancelled) navigate("/login", { replace: true });
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  useEffect(() => {
    axios
      .get(`${API}/categories`)
      .then((res) => setCategories(res.data || []))
      .catch(() => setCategories([]));
  }, []);

  // Ids only — the profile needs a count, not the products themselves.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/bookmarks/ids`, { withCredentials: true })
      .then((res) => !cancelled && setBookmarkCount((res.data?.product_ids || []).length))
      .catch(() => !cancelled && setBookmarkCount(null));
    return () => {
      cancelled = true;
    };
  }, []);

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  // Height options come from the user's gender category (set at onboarding);
  // users without a gender get the union. The current height is always included.
  const catName = user ? GENDER_TO_CATEGORY[user.gender] : null;
  const matched = catName
    ? categories.find((c) => (c.name || "").toLowerCase() === catName)
    : null;
  const baseHeights = matched?.heights?.length
    ? matched.heights
    : categories.flatMap((c) => c.heights || []);
  const heightOptions = sortHeights([
    ...new Set([...baseHeights, ...(user?.height ? [user.height] : [])]),
  ]);

  const usernameTrimmed = form.username.trim();
  const usernameChanged = user && usernameTrimmed !== (user.username || "");
  const usernameInvalid = usernameChanged && !USERNAME_RE.test(usernameTrimmed);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (usernameInvalid) {
      toast.error("Usernames must be 3-20 letters, no numbers or special characters");
      return;
    }
    const profileChanges = {};
    if (form.name.trim() !== (user.name || "")) profileChanges.name = form.name;
    if (form.height !== (user.height || "")) profileChanges.height = form.height;
    if (form.weight !== (user.weight || "")) profileChanges.weight = form.weight;
    if (form.weight_unit !== (user.weight_unit || "lbs")) profileChanges.weight_unit = form.weight_unit;
    if (form.waist_size !== (user.waist_size || "")) profileChanges.waist_size = form.waist_size;
    if (form.shoe_size !== (user.shoe_size || "")) profileChanges.shoe_size = form.shoe_size;
    if (form.city !== (user.city || "")) profileChanges.city = form.city;

    if (!usernameChanged && Object.keys(profileChanges).length === 0) {
      toast.info("Nothing to update");
      return;
    }

    setSaving(true);
    try {
      let updated = { ...user };
      if (usernameChanged) {
        const res = await axios.put(
          `${API}/me/username`,
          { username: usernameTrimmed },
          { withCredentials: true }
        );
        updated.username = res.data.username;
      }
      if (Object.keys(profileChanges).length > 0) {
        const res = await axios.put(`${API}/me/profile`, profileChanges, {
          withCredentials: true,
        });
        updated = { ...updated, ...res.data.user };
      }
      setUser(updated);
      // Which fields people revise matters (a changed height re-targets both the
      // feed and the push campaigns), so send the field names — never the values.
      track("profile_update", {
        fields_changed: [
          ...(usernameChanged ? ["username"] : []),
          ...Object.keys(profileChanges),
        ].join(","),
        field_count: Object.keys(profileChanges).length + (usernameChanged ? 1 : 0),
      });
      setAnalyticsUser(updated);
      toast.success("Profile updated");
    } catch (err) {
      console.error("Profile update failed:", err);
      track("profile_update_failed", { error_status: err?.response?.status });
      toast.error(err.response?.data?.detail || "Could not update your profile");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    // GA4 has no recommended logout event, so this stays custom. Fired before
    // the request so it still lands if the page unloads early; the identity is
    // cleared straight after so nothing later is attributed to this user.
    track("logout");
    setAnalyticsUser(null);
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      toast.success("Logged out successfully");
    } catch (e) {
      console.error("Logout error:", e);
    }
    // Hard reload to the feed so every component resyncs to the logged-out state.
    window.location.assign("/");
  };

  const Avatar = () =>
    user?.picture ? (
      <img
        src={user.picture}
        alt="Your avatar"
        className="w-24 h-24 rounded-full object-cover ring-1 ring-[#E4E1D9]"
      />
    ) : (
      <span
        className="w-24 h-24 rounded-full bg-[#2D302A] text-white flex items-center justify-center text-3xl uppercase"
        style={mono}
      >
        {(user?.username || "?").charAt(0)}
      </span>
    );

  return (
    <div className="min-h-screen bg-[#FAF9F5]">
      {/* Header */}
      <header className="border-b border-[#E4E1D9] px-6 md:px-12 py-4 flex items-center gap-4">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-[#6B6A68] hover:text-[#1A1A1A] transition-colors text-xs uppercase tracking-[0.2em]"
          style={mono}
          data-testid="profile-back"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <h1
          className="text-2xl md:text-3xl tracking-tighter ml-2"
          style={{ fontFamily: "Cormorant Garamond, serif" }}
        >
          Profile
        </h1>
      </header>

      <main className="max-w-md mx-auto px-6 py-10 md:py-14">
        {loading ? (
          <div className="flex flex-col items-center gap-4 animate-pulse">
            <div className="w-24 h-24 rounded-full bg-[#E4E1D9]" />
            <div className="h-5 w-40 bg-[#E4E1D9] rounded" />
            <div className="h-3 w-56 bg-[#F0EFEB] rounded" />
          </div>
        ) : user ? (
          <>
            {/* Identity header */}
            <div className="flex flex-col items-center text-center mb-10">
              <Avatar />
              <h2
                className="mt-5 text-3xl tracking-tighter text-[#2D302A]"
                style={{ fontFamily: "Cormorant Garamond, serif" }}
                data-testid="profile-username"
              >
                {user.username || "Set a username"}
              </h2>
              <p className="mt-1 text-sm text-[#6B6A68]">{user.email}</p>
            </div>

            {/* Saved — bookmarks moved under the Profile module, so this is
                now the way in. The list itself still lives at /bookmarks. */}
            <button
              type="button"
              onClick={() => {
                track("nav_click", { nav_item: "saved", destination: "/bookmarks", is_logged_in: true });
                navigate("/bookmarks");
              }}
              className="w-full flex items-center gap-3 bg-white border border-[#E4E1D9] rounded-md px-4 py-4 mb-10 hover:border-[#C9C5BA] transition-colors"
              data-testid="profile-bookmarks-link"
            >
              <BookmarkSimple size={20} className="shrink-0 text-[#2D302A]" />
              <span className="flex-1 text-left">
                <span className="block text-sm text-[#2D302A]">Your bookmarks</span>
                <span className="block text-[11px] text-[#9A9A98]" style={mono}>
                  {bookmarkCount === null
                    ? "Everything you've saved"
                    : bookmarkCount === 1
                    ? "1 saved item"
                    : `${bookmarkCount} saved items`}
                </span>
              </span>
              <CaretRight size={16} className="shrink-0 text-[#9A9A98]" />
            </button>

            {/* Editable details */}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className={labelCls} style={mono}>Username</label>
                <input
                  type="text"
                  value={form.username}
                  onChange={set("username")}
                  maxLength={20}
                  className={inputCls}
                  data-testid="profile-username-input"
                />
                <p
                  className={`mt-1 text-[11px] ${usernameInvalid ? "text-red-600" : "text-[#9A9A98]"}`}
                  style={mono}
                >
                  Shown publicly. 3-20 letters, no numbers or special characters.
                </p>
              </div>

              <div>
                <label className={labelCls} style={mono}>Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={set("name")}
                  maxLength={80}
                  className={inputCls}
                  data-testid="profile-name-input"
                />
                <p className="mt-1 text-[11px] text-[#9A9A98]" style={mono}>
                  Private. Never shown to the community.
                </p>
              </div>

              <div>
                <label className={labelCls} style={mono}>City</label>
                <CityInput
                  value={form.city}
                  onChange={(v) => setForm((prev) => ({ ...prev, city: v }))}
                  className={inputCls}
                  testId="profile-city-input"
                />
                <p className="mt-1 text-[11px] text-[#9A9A98]" style={mono}>
                  Used to show you offline spots near you. Leave blank to see every city.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls} style={mono}>Height</label>
                  <select
                    value={form.height}
                    onChange={set("height")}
                    className={inputCls}
                    data-testid="profile-height-select"
                  >
                    <option value="">Select…</option>
                    {heightOptions.map((h) => (
                      <option key={h} value={h}>{h} ft</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls} style={mono}>Weight</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={form.weight}
                      onChange={set("weight")}
                      className={`${inputCls} min-w-0`}
                      data-testid="profile-weight-input"
                    />
                    <select
                      value={form.weight_unit}
                      onChange={set("weight_unit")}
                      className={`${inputCls} w-24`}
                      data-testid="profile-weight-unit"
                    >
                      <option value="lbs">lbs</option>
                      <option value="kg">kg</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls} style={mono}>Waist (in)</label>
                  <select
                    value={form.waist_size}
                    onChange={set("waist_size")}
                    className={inputCls}
                    data-testid="profile-waist-select"
                  >
                    <option value="">Select…</option>
                    {WAIST_SIZES.map((w) => (
                      <option key={w} value={w}>{w} in</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls} style={mono}>Shoe size (UK)</label>
                  <select
                    value={form.shoe_size}
                    onChange={set("shoe_size")}
                    className={inputCls}
                    data-testid="profile-shoe-select"
                  >
                    <option value="">Select…</option>
                    {SHOE_SIZES.map((s) => (
                      <option key={s} value={s}>UK {s}</option>
                    ))}
                  </select>
                </div>
              </div>

              <button
                type="submit"
                disabled={saving || usernameInvalid}
                className="w-full bg-[#2D302A] text-white py-3 rounded-md text-xs uppercase tracking-[0.25em] hover:bg-[#1A1C18] transition-colors disabled:opacity-50"
                style={mono}
                data-testid="profile-save-button"
              >
                {saving ? "Saving..." : "Save profile"}
              </button>
            </form>

            {/* Settings — the in-app way into About / Contact / Privacy / Terms. */}
            <button
              type="button"
              onClick={() => {
                track("nav_click", { nav_item: "settings", destination: "/settings", is_logged_in: true });
                navigate("/settings");
              }}
              className="w-full flex items-center gap-3 bg-white border border-[#E4E1D9] rounded-md px-4 py-4 mt-8 hover:border-[#C9C5BA] transition-colors"
              data-testid="profile-settings-link"
            >
              <GearSix size={20} className="shrink-0 text-[#2D302A]" />
              <span className="flex-1 text-left">
                <span className="block text-sm text-[#2D302A]">Settings</span>
                <span className="block text-[11px] text-[#9A9A98]" style={mono}>
                  About, contact, privacy & terms
                </span>
              </span>
              <CaretRight size={16} className="shrink-0 text-[#9A9A98]" />
            </button>

            {/* Log out */}
            <div className="mt-8 pt-6 border-t border-[#E4E1D9]">
              <button
                type="button"
                onClick={handleLogout}
                disabled={loggingOut}
                className="w-full flex items-center justify-center gap-2 border border-[#E4E1D9] text-[#B23A48] py-3 rounded-md text-xs uppercase tracking-[0.25em] hover:bg-[#F1E4E5] hover:border-[#B23A48] transition-colors disabled:opacity-50"
                style={mono}
                data-testid="profile-logout-button"
              >
                <SignOut size={18} weight="bold" />
                {loggingOut ? "Logging out..." : "Log out"}
              </button>
            </div>
          </>
        ) : null}
      </main>
    </div>
  );
};

export default Profile;
